import { change, ChangeState, createChangeState } from "@greeter/changes";
import { SpecialPricePeriod } from "@greeter/core";
import { createFetchMachine, FetchActor } from "@greeter/xstate-util";
import { setup, assign } from "xstate";
import { indexBy } from "@greeter/map";
import { addDays } from "date-fns";
import { IVenueApi } from "@greeter/venue-api";
import * as SetUtil from "@greeter/set";

export type SpecialPricePeriodApiRequirements = Pick<
  IVenueApi,
  | "fetchVenue"
  | "fetchSpecialPricePeriodsByProducts"
  | "fetchSpecialOpeningHours"
>;

export type SpecialPricePeriodChange =
  | { type: "set"; data: SpecialPricePeriod }
  | { type: "setStartsAt"; data: { id: string; date: Date } }
  | { type: "setEndsAt"; data: { id: string; date: Date } }
  | { type: "setPrice"; data: { id: string; price: number } }
  | { type: "add"; data: { id: string } }
  | { type: "delete"; data: string };

function setupApply(context: SpecialPricePeriodEditorContext) {
  return function apply(
    m: Map<string, SpecialPricePeriod>,
    c: SpecialPricePeriodChange
  ) {
    switch (c.type) {
      case "set": {
        const item = m.get(c.data.id);
        if (item) {
          item.price = c.data.price;
          item.period = c.data.period;
        }
        break;
      }

      case "delete":
        m.delete(c.data);
        break;

      case "add": {
        const latest = [...m.values()]
          .sort((a, b) => Number(b.period.from) - Number(a.period.from))
          .at(-1);
        const from = latest ? addDays(latest.period.to, 1) : new Date();
        const to = latest ? addDays(latest.period.to, 1) : new Date();
        m.set(c.data.id, {
          id: c.data.id,
          period: { from: from, to: to },
          productId: context.productId,
          price: 0,
        });
        break;
      }

      case "setPrice": {
        const specialPricePeriod = m.get(c.data.id);
        if (specialPricePeriod) {
          specialPricePeriod.price = c.data.price;
        }
        break;
      }

      case "setStartsAt": {
        const specialPricePeriod = m.get(c.data.id);
        if (specialPricePeriod) {
          specialPricePeriod.period.from = c.data.date;
        }
        break;
      }

      case "setEndsAt": {
        const specialPricePeriod = m.get(c.data.id);
        if (specialPricePeriod) {
          specialPricePeriod.period.to = c.data.date;
        }
        break;
      }
    }
  };
}

export function filterOutDeleteChanges(
  changes: Array<SpecialPricePeriodChange>
) {
  return changes.filter((c) => c.type !== "delete");
}

export function findSpecialPricePeriodsToDelete(
  changes: Array<SpecialPricePeriodChange>
): Array<string> {
  const added = new Set(
    changes.filter((c) => c.type === "add").map((c) => c.data.id)
  );
  const deleted = new Set(
    changes.filter((c) => c.type === "delete").map((c) => c.data)
  );
  const toDelete = SetUtil.difference(deleted, added);
  return Array.from(toDelete);
}

type SpecialPriceChangeState = ChangeState<
  Map<string, SpecialPricePeriod>,
  SpecialPricePeriodChange
>;

type InternalSpecialPricePeriodEditorEvents = {
  type: "internal.setChanges";
  data: SpecialPriceChangeState;
};

type SpecialPricePeriodEditorEvents =
  | InternalSpecialPricePeriodEditorEvents
  | { type: "change"; data: SpecialPricePeriodChange }
  | { type: "fetch"; data: { productId: string } }
  | { type: "open" }
  | { type: "close" }
  | { type: "reset" };

export type SpecialPricePeriodEditorContext = {
  mode: "update" | "create";
  changes: SpecialPriceChangeState;
  productId: string;

  venueRef: FetchActor<SpecialPricePeriodApiRequirements["fetchVenue"]>;
  specialPricePeriodRef: FetchActor<
    SpecialPricePeriodApiRequirements["fetchSpecialPricePeriodsByProducts"]
  >;
  specialOpeningHoursRef: FetchActor<
    SpecialPricePeriodApiRequirements["fetchSpecialOpeningHours"]
  >;
};

export const specialPricePeriodEditorMachine = setup({
  types: {} as {
    context: SpecialPricePeriodEditorContext;
    events: SpecialPricePeriodEditorEvents;
    emitted: { type: "saved" };
    input: {
      productId: string;
      mode: SpecialPricePeriodEditorContext["mode"];
      api: SpecialPricePeriodApiRequirements;
    };
  },
}).createMachine({
  context({ input: { productId, mode, api }, spawn, self }) {
    const specialPricePeriodRef = spawn(
      createFetchMachine(api.fetchSpecialPricePeriodsByProducts.bind(api))
    );
    specialPricePeriodRef.send({ type: "fetch", data: [[productId]] });
    specialPricePeriodRef.on("fetched", (event) => {
      self.send({
        type: "internal.setChanges",
        data: createChangeState(indexBy(event.data, (p) => p.id)),
      });
    });

    const venueRef = spawn(createFetchMachine(api.fetchVenue.bind(api)));
    venueRef.send({ type: "fetch", data: [] });

    const specialOpeningHoursRef = spawn(
      createFetchMachine(api.fetchSpecialOpeningHours.bind(api))
    );
    specialOpeningHoursRef.send({ type: "fetch", data: [] });

    return {
      mode,
      changes: createChangeState(new Map<string, SpecialPricePeriod>()),
      productId,
      venueRef,
      specialOpeningHoursRef,
      specialPricePeriodRef,
    };
  },
  on: {
    "internal.setChanges": {
      actions: assign(({ event }) => ({ changes: event.data })),
    },

    reset: {
      actions: assign({
        changes: () => createChangeState(new Map<string, SpecialPricePeriod>()),
      }),
    },

    change: {
      actions: assign(({ event, context }) => {
        return {
          changes: change(context.changes, setupApply(context), event.data),
        };
      }),
    },

    fetch: {
      actions: ({ context, event }) =>
        context.specialPricePeriodRef.send({
          type: "fetch",
          data: [[event.data.productId]],
        }),
    },
  },
  initial: "closed",
  states: {
    closed: {
      on: {
        open: "opened",
      },
    },
    opened: {
      on: {
        close: "closed",
      },
    },
  },
});
